/**
 * Bloq public booking page — Calendly-style 3-column layout.
 *
 * Layout:
 *   ┌──────────────┬──────────────────────┬───────────────────┐
 *   │  Event/Host  │   Monthly calendar   │  Available times  │
 *   │  details     │   (scroll up to 1 yr)│  (scrollbar if      │
 *   │              │                      │   overflows)      │
 *   └──────────────┴──────────────────────┴───────────────────┘
 *
 * - White background.
 * - Calendly-style: dates with 0 availability are dimmed/disabled.
 * - Prominent timezone selector at the top of the right column.
 * - Auto-detect user timezone from browser.
 * - In-person/hybrid: shows an embedded map of the address.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, CalendarCheck, Clock, MapPin, Globe, ChevronLeft, ChevronRight,
  Video, Phone, Building2, ExternalLink, HelpCircle, AlertCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { RichTextView } from "@/components/RichTextEditor";
import { AddressFields } from "@/components/AddressFields";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  COMMON_TIMEZONES, userBrowserTimezone, tzLabelWithOffset,
  formatLongDateInTz, formatTime12hInTz, tzLabel as tzShort,
  formatLongDateTimeInTz,
} from "@/lib/time";

// --- Calendar helpers --------------------------------------------------------
function startOfMonth(d) {
  const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x;
}
function addMonths(d, n) {
  const x = new Date(d); x.setMonth(x.getMonth() + n); return x;
}
function isoDay(d, tz) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
}
/**
 * Format a duration in minutes into a human-friendly string.
 *   45  → "45 min"
 *   60  → "1 hr"
 *   75  → "1 hr 15 min"
 *   120 → "2 hrs"
 */
function formatDuration(mins) {
  const m = Math.max(0, Number(mins) || 0);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  const hLabel = h === 1 ? "1 hr" : `${h} hrs`;
  return rem ? `${hLabel} ${rem} min` : hLabel;
}

function buildMonthGrid(viewDate, tz) {
  // Build a 6x7 grid of dates (Sun..Sat) covering the visible month in `tz`.
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekday = first.getDay(); // 0=Sun
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(first);
    d.setDate(first.getDate() - startWeekday + i);
    days.push({ date: d, key: isoDay(d, tz), inMonth: d.getMonth() === viewDate.getMonth() });
  }
  return days;
}

const LOCATION_ICON = {
  bloq_video: Video,
  phone: Phone,
  in_person: Building2,
  hybrid: Building2,
  custom: MapPin,
  external_link: ExternalLink,
  tbd: HelpCircle,
};

// Map appointment_location.type → icon component. Reuses LOCATION_ICON
// where the values overlap (in_person/online/phone) so the visual
// language stays consistent across the link's location_type and the
// appointment's location_ids[] picks.
const APT_LOC_ICON = {
  online: Video,
  in_person: Building2,
  hybrid: Building2,
  phone: Phone,
  custom: MapPin,
};
const APT_LOC_FRIENDLY = {
  online: "Online (video)",
  in_person: "In person",
  hybrid: "Hybrid (video + in person)",
  phone: "Phone call",
  custom: "Custom",
};

const LOCATION_FRIENDLY = {
  bloq_video: "Bloq video meeting",
  phone: "Phone call",
  in_person: "In person",
  hybrid: "Hybrid (video + in person)",
  custom: "Custom location",
  external_link: "External link",
  tbd: "Location to be determined",
};

export default function PublicBookingV2({ mode = "link" }) {
  // mode: "link" → /book/:slug (booking link)
  //       "appointment" → /a/:slug (direct appointment)
  // The data shape is identical thanks to _synthetic_link_from_apt on the
  // backend; only the API path differs.
  const { slug } = useParams();
  const apiBase = mode === "appointment" ? "/booking/public/appointments" : "/booking/public/links";
  const [info, setInfo] = useState(null);
  const [legacyInfo, setLegacyInfo] = useState(null);
  const [error, setError] = useState(null);

  // Auto-detect TZ from browser
  const [tz, setTz] = useState(userBrowserTimezone());
  const [viewDate, setViewDate] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [monthSlots, setMonthSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const calendarRef = useRef(null);
  const timesRef = useRef(null);

  // Smooth-scroll the times pane into view when a date is tapped on mobile.
  // Desktop has the times pane sitting right next to the calendar (lg+
  // 2-col grid), so no scroll needed there. v2.87 polish.
  const scrollToTimesOnMobile = () => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 1024) return; // lg breakpoint
    // Wait one frame so the day's selected state has rendered.
    requestAnimationFrame(() => {
      timesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const [step, setStep] = useState("pick");
  const [formValues, setFormValues] = useState({});
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Bundle B — multi-appointment-type links. When the link contains 2+
  // appointment types, render a step-0 picker. The selected appt type
  // overrides the link's duration / price / form / addons for the rest
  // of the flow.
  const apptTypes = info?.appointment_types || [];
  const [selectedAptId, setSelectedAptId] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  // The two flags below track whether the visitor has EXPLICITLY clicked
  // an appointment-type / location button. We need this distinction
  // because:
  //   • Single-AT links auto-select the only AT on mount.
  //   • Single-location appts auto-select the only location on mount.
  //   • Multi-AT links back-fill `info.appointment_type` from the backend,
  //     which makes the derived `apt` value truthy before the visitor
  //     has chosen anything.
  // The left aside's duration / location / price block + the in-person
  // map block both gate on these flags so nothing leaks into the aside
  // until the visitor actually picks. Reset to false in the "Change"
  // handlers and on info reload.
  const [userPickedApt, setUserPickedApt] = useState(false);
  const [userPickedLocation, setUserPickedLocation] = useState(false);
  // Auto-pick when there's only one type so single-type links keep the
  // existing visual flow (no step-0 card).
  useEffect(() => {
    if (apptTypes.length === 1) setSelectedAptId(apptTypes[0].appointment_type_id);
    else if (apptTypes.length > 1) setSelectedAptId(null);
  }, [apptTypes.length, info?.link_id]);
  const selectedApt = apptTypes.find((a) => a.appointment_type_id === selectedAptId) || info?.appointment_type || null;
  // Locations the visitor can pick from for the SELECTED appointment.
  const allLocations = info?.locations || [];
  const aptLocationIds = selectedApt?.location_ids || [];
  const aptLocations = aptLocationIds
    .map((id) => allLocations.find((l) => l.location_id === id))
    .filter(Boolean);
  const needsLocationPick = aptLocations.length > 1 && !selectedLocationId;
  // Auto-pick the default/sole location so single-location appts skip the
  // picker entirely (no UX regression for existing flows).
  useEffect(() => {
    if (!selectedApt) { setSelectedLocationId(null); return; }
    if (aptLocations.length === 0) { setSelectedLocationId(null); return; }
    if (aptLocations.length === 1) {
      setSelectedLocationId(aptLocations[0].location_id);
    } else {
      const dflt = selectedApt.default_location_id;
      if (dflt && aptLocations.find((l) => l.location_id === dflt)) {
        // Don't auto-apply default — let the visitor see the choice.
        setSelectedLocationId(null);
      } else {
        setSelectedLocationId(null);
      }
    }
  }, [selectedAptId, aptLocations.length, selectedApt?.default_location_id]);

  // Load link info
  useEffect(() => {
    // Reset user-pick flags on slug/info change so re-navigating doesn't
    // leak a stale "I clicked it" state from a previous page view.
    setUserPickedApt(false);
    setUserPickedLocation(false);
    api.get(`${apiBase}/${slug}`).then((r) => setInfo(r.data)).catch(async () => {
      // Fallback: try legacy public booking only when we're in link mode
      if (mode !== "appointment") {
        try {
          const r = await api.get(`/bookings/public/${slug}`);
          setLegacyInfo(r.data);
        } catch {
          setError("This booking link is no longer active.");
        }
      } else {
        setError("This appointment is no longer available.");
      }
    });
  }, [slug, mode, apiBase]);

  // Determine the booking window cap (default 60d, max 1 year). Backend exposes
  // `maximum_days_in_future` so we can compute the latest month the user may scroll to.
  const maxFutureMonths = useMemo(() => {
    const days = Number(info?.maximum_days_in_future || 365);
    return Math.max(1, Math.min(12, Math.ceil(days / 30) + 1));
  }, [info]);

  // Fetch availability for the visible month + invitee timezone. We
  // delay fetching until the visitor has picked an appointment type on
  // multi-type links (avoids showing wrong slots from the first type).
  useEffect(() => {
    if (!info) return;
    if (apptTypes.length > 1 && !selectedAptId) {
      setMonthSlots([]);
      return;
    }
    setLoadingSlots(true);
    const start = startOfMonth(viewDate).toISOString();
    const end = addMonths(startOfMonth(viewDate), 1).toISOString();
    const ids = selectedAddons.join(",");
    api.get(`${apiBase}/${slug}/availability`, {
      params: {
        start, end,
        invitee_timezone: tz,
        addon_ids: ids || undefined,
        // appointment_type_id only matters for booking-link mode; direct
        // appointment URLs already resolve their type via the slug.
        appointment_type_id: mode === "appointment" ? undefined : (selectedAptId || undefined),
      },
    }).then((r) => setMonthSlots(r.data.slots || []))
      .catch(() => setMonthSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [info, slug, tz, viewDate, selectedAddons, selectedAptId, apptTypes.length, mode, apiBase]);

  // Measure calendar height so the right column matches its scroll cap.
  useEffect(() => {
    if (!calendarRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setCalendarHeight(e.contentRect.height);
    });
    ro.observe(calendarRef.current);
    return () => ro.disconnect();
  }, []);

  // Group slots by date key in invitee tz
  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of monthSlots) {
      const key = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date(s.start_utc));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return map;
  }, [monthSlots, tz]);

  const grid = useMemo(() => buildMonthGrid(viewDate, tz), [viewDate, tz]);
  const today = useMemo(() => isoDay(new Date(), tz), [tz]);

  if (error) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-10 max-w-md mx-auto text-center bg-white border border-[#1a1a1f]/8">
          <p className="bloq-headline text-2xl">Link unavailable</p>
          <p className="text-sm text-[#82828c] mt-3">{error}</p>
        </div>
      </PublicShell>
    );
  }

  if (!info && legacyInfo) {
    return (
      <PublicShell>
        <div className="rounded-2xl p-8 max-w-xl mx-auto bg-white border border-[#1a1a1f]/8">
          <p className="bloq-headline text-2xl">{legacyInfo.title}</p>
          <p className="text-sm text-[#82828c] mt-2">{legacyInfo.description}</p>
          <p className="text-sm mt-4">
            This booking link uses a simple flow. <Link to={`/book/${slug}?legacy=1`} className="text-[#005e7a] underline">Continue here</Link>.
          </p>
        </div>
      </PublicShell>
    );
  }

  if (!info) {
    return <PublicShell><div className="text-center text-sm text-[#82828c]">Loading…</div></PublicShell>;
  }

  const apt = selectedApt || info.appointment_type;
  // Pick the form to render based on the currently-selected AT (multi-AT
  // booking links). Falls back to the single legacy `form` field for direct
  // appointment links and clients pre-form_by_apt_id deploys.
  const currentForm = (info.form_by_apt_id && selectedAptId && info.form_by_apt_id[selectedAptId])
    ? info.form_by_apt_id[selectedAptId]
    : info.form;
  const fields = currentForm?.fields || [];
  // For multi-type links, filter addons to those attached to the selected
  // appt type so the visitor only sees applicable ones.
  const allAddons = info.addons || [];
  const addons = selectedAptId
    ? allAddons.filter((ad) =>
        !Array.isArray(ad.appointment_type_ids) ||
        ad.appointment_type_ids.length === 0 ||
        ad.appointment_type_ids.includes(selectedAptId))
    : allAddons;
  const hostTz = info.host_timezone || info.timezone || "UTC";
  // Host timezone is intentionally NOT exposed in the public booking UI — it confuses invitees.

  // ── Resolve the EFFECTIVE location for display. Priority:
  //    1. Visitor's pick (when multiple locations attached to appt)
  //    2. Appt's default_location_id
  //    3. Appt's first location in location_ids[]
  //    4. Fallback to the link-level location_type (legacy / no appt)
  const effectiveLocation = (() => {
    if (selectedLocationId) {
      const m = allLocations.find((l) => l.location_id === selectedLocationId);
      if (m) return { source: "appt_location", record: m };
    }
    if (apt?.default_location_id) {
      const m = allLocations.find((l) => l.location_id === apt.default_location_id);
      if (m) return { source: "appt_location", record: m };
    }
    if (aptLocations.length === 1) {
      return { source: "appt_location", record: aptLocations[0] };
    }
    return { source: "link", record: null };
  })();
  // Pick the appropriate icon component for the effective location.
  const EffectiveLocIcon = effectiveLocation.record
    ? (APT_LOC_ICON[effectiveLocation.record.type] || MapPin)
    : (LOCATION_ICON[info.location_type] || MapPin);
  const effectiveLocationLabel = effectiveLocation.record
    ? effectiveLocation.record.name
    : (LOCATION_FRIENDLY[info.location_type] || info.location_type);
  // The "Address" sub-text under the Location label is type-aware so each
  // booking type renders the actionable detail rather than a generic label:
  //   • phone           → host's typed phone_instructions
  //   • online + ext.   → the typed external_link URL
  //   • online + bloq   → blank (the Bloq meeting URL doesn't exist until
  //                       AFTER the booking is submitted)
  //   • in_person/etc.  → the saved physical address
  const effectiveLocationSub = effectiveLocation.record
    ? (
        effectiveLocation.record.type === "phone"
          ? (effectiveLocation.record.phone_instructions || "")
        : (effectiveLocation.record.type === "online"
           && effectiveLocation.record.online_behavior === "external_link")
          ? (effectiveLocation.record.external_link || "")
        : (effectiveLocation.record.type === "online")
          ? ""
        : (effectiveLocation.record.address_formatted
           || effectiveLocation.record.address
           || "")
      )
    : (info.location_address_formatted || "");

  // --- Booking submit ---------------------------------------------------------
  const handleSubmit = async () => {
    if (!selectedSlot) { toast.error("Pick a time"); return; }
    if (!(formValues._first_name || "").trim()) { toast.error("First name is required"); return; }
    if (!(formValues._last_name || "").trim()) { toast.error("Last name is required"); return; }
    if (!(formValues._email || "").trim()) { toast.error("Email is required"); return; }
    if (apt?.phone_required && !(formValues._phone || "").trim()) {
      toast.error("Phone is required"); return;
    }
    const requiredMissing = fields.filter((f) => f.required && !formValues[f.field_id]).map((f) => f.label);
    if (requiredMissing.length) { toast.error(`Missing required: ${requiredMissing.join(", ")}`); return; }
    const responses = fields.map((f) => ({ field_id: f.field_id, label: f.label, value: formValues[f.field_id] }));
    const payload = {
      invitee_first_name: (formValues._first_name || "").trim(),
      invitee_last_name: (formValues._last_name || "").trim(),
      invitee_name: `${(formValues._first_name || "").trim()} ${(formValues._last_name || "").trim()}`.trim(),
      invitee_email: formValues._email || "",
      invitee_phone: formValues._phone || "",
      invitee_timezone: tz,
      start_time_utc: selectedSlot.start_utc,
      notes: formValues._notes || "",
      form_responses: responses,
      addon_ids: selectedAddons,
      // Bundle B: multi-type links must tell the server which appt was chosen.
      appointment_type_id: selectedAptId || undefined,
      // Bundle B.1: pass the visitor's chosen location when the appt has multiple.
      selected_location_id: selectedLocationId || undefined,
      source: "public_page",
    };
    setSubmitting(true);
    try {
      const r = await api.post(`${apiBase}/${slug}/submit`, payload);
      // Phase B.2 — when the appointment requires a deposit/full payment,
      // the backend returns a Stripe Checkout URL we redirect to. After
      // the visitor pays, Stripe sends them back to /booking/payment/success
      // which polls the backend until the booking is confirmed.
      if (r.data?.payment_required && r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setDone(r.data);
      setStep("done");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  // ---- DONE step -------------------------------------------------------------
  if (step === "done") {
    // Prefer backend-resolved location (consistent with emails/ICS/admin).
    // Falls back to the locally-derived `effectiveLocation` for legacy
    // submits where the backend didn't echo back the location.
    const DoneLocIcon = done.location_type
      ? (
          done.location_type === "bloq_video" ? Video
          : done.location_type === "phone" ? Phone
          : done.location_type === "in_person" ? Building2
          : done.location_type === "hybrid" ? Building2
          : done.location_type === "external_link" ? ExternalLink
          : done.location_type === "tbd" ? HelpCircle
          : MapPin
        )
      : EffectiveLocIcon;
    const doneLocLabel = done.location_label || effectiveLocationLabel;
    const doneLocSub = done.location_address || effectiveLocationSub;
    const doneAptName = done.appointment_type_name || apt?.name || "";
    const doneDuration = done.duration_minutes || apt?.duration_minutes;
    return (
      <PublicShell>
        <StepStrip
          steps={buildStepList({ apptTypes, aptLocations })}
          currentStep="done"
          onNavigate={() => {}}
        />
        <div className="rounded-2xl p-10 max-w-3xl mx-auto bg-white border border-[#1a1a1f]/8 shadow-sm">
          <CalendarCheck className="w-14 h-14 text-[#005e7a] mb-5" />
          <p className="bloq-headline text-4xl mb-2">
            {done.status === "confirmed" ? "You're booked!" : done.status === "pending" ? "Request submitted" : "Submitted"}
          </p>

          <div className="mt-6 space-y-4 text-base">
            {doneAptName && (
              <div className="flex items-start gap-3">
                <CalendarCheck className="w-5 h-5 text-[#005e7a] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-[#1a1a1f] text-lg">{doneAptName}</p>
                  {doneDuration && <p className="text-base text-[#82828c] mt-0.5">{formatDuration(doneDuration)}</p>}
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#005e7a] mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-[#1a1a1f] text-lg">{done.invitee_local_start_long}</p>
                <p className="text-base text-[#82828c] mt-0.5">Your timezone: {tz} ({tzShort(done.start_time_utc, tz)})</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <DoneLocIcon className="w-5 h-5 text-[#005e7a] mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-[#1a1a1f] text-lg">{doneLocLabel}</p>
                {doneLocSub && (
                  <p className="text-base text-[#4a4a52] mt-0.5">{doneLocSub}</p>
                )}
              </div>
            </div>
          </div>

          {done.status === "pending" && done.requires_payment_setup && (
            <p className="text-base text-[#b45309] mt-5 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Payment is required but the host hasn't configured Stripe yet. They will follow up manually.</p>
          )}
          {done.status === "pending" && !done.requires_payment_setup && (
            <p className="text-base text-[#4a4a52] mt-5">Your request is awaiting approval. You'll be notified once it's confirmed.</p>
          )}
          {done.confirmation_message && <p className="text-base mt-5 bg-[#f5efe6] p-4 rounded-lg whitespace-pre-line">{done.confirmation_message}</p>}
          {done.meeting_public_id && (
            <Link to={`/m/${done.meeting_public_id}`} className="bloq-btn-primary inline-flex items-center gap-2 mt-6 text-base px-6 py-3" data-testid="pub-open-room">
              <ArrowRight className="w-5 h-5" /> Open meeting room
            </Link>
          )}
          <div className="mt-7 pt-6 border-t border-[#1a1a1f]/06 text-base text-[#4a4a52] space-y-1.5">
            <p className="font-medium text-[#1a1a1f]">Need to manage this booking?</p>
            {info.allow_reschedule && <p><Link to={`/booking-manage/${done.secure_reschedule_token}?action=reschedule`} className="text-[#005e7a] underline font-medium">Reschedule</Link></p>}
            {info.allow_cancel && <p><Link to={`/booking-manage/${done.secure_cancel_token}?action=cancel`} className="text-[#005e7a] underline font-medium">Cancel</Link></p>}
          </div>
        </div>
      </PublicShell>
    );
  }

  // ---- FORM step -------------------------------------------------------------
  if (step === "form") {
    return (
      <PublicShell>
        <div className="rounded-2xl p-8 max-w-3xl mx-auto bg-white border border-[#1a1a1f]/8 shadow-sm">
          <StepStrip
            steps={buildStepList({ apptTypes, aptLocations })}
            currentStep="form"
            onNavigate={(target) => navigateToStep({
              target,
              setStep,
              setSelectedSlot,
              setSelectedDay,
              setSelectedAptId,
              setSelectedLocationId,
              apptTypes,
              aptLocations,
            })}
          />
          <button onClick={() => setStep("pick")} className="text-base text-[#82828c] inline-flex items-center gap-1.5 mb-6 hover:text-[#005e7a]" data-testid="pub-back">
            <ChevronLeft className="w-4 h-4" /> Back to schedule
          </button>
          <p className="bloq-headline text-3xl">Confirm booking</p>
          {apt && (
            <div className="bloq-pill inline-flex text-sm mt-3" style={{ backgroundColor: (apt.color || "#005e7a") + "1a", color: apt.color || "#005e7a" }}>
              {apt.name} · {formatDuration(apt.duration_minutes)}
            </div>
          )}
          <div className="mt-4 space-y-2 text-base text-[#4a4a52]">
            <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-[#005e7a]" /> {formatLongDateTimeInTz(selectedSlot.start_utc, tz)}</div>
            <div className="text-sm text-[#82828c] pl-7">
              Your timezone: {tz} ({tzShort(selectedSlot.start_utc, tz)})
            </div>
            <div className="flex items-start gap-2">
              <EffectiveLocIcon className="w-5 h-5 text-[#005e7a] mt-0.5 flex-shrink-0" />
              <span className="flex flex-col">
                <span>{effectiveLocationLabel}</span>
                {effectiveLocationSub && <span className="text-sm text-[#82828c]">{effectiveLocationSub}</span>}
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-6">
            <FieldInput label="First name" required value={formValues._first_name || ""} onChange={(v) => setFormValues({ ...formValues, _first_name: v })} testid="pub-firstname" />
            <FieldInput label="Last name" required value={formValues._last_name || ""} onChange={(v) => setFormValues({ ...formValues, _last_name: v })} testid="pub-lastname" />
            <FieldInput type="email" label="Email" required value={formValues._email || ""} onChange={(v) => setFormValues({ ...formValues, _email: v })} testid="pub-email" />
            {/* Phone — restrict input to digits + standard phone-format
                characters (+, -, (, ), space). Strips any letters or other
                symbols the moment they're typed so the value submitted is
                always a clean phone string. */}
            <FieldInput
              label={apt?.phone_required ? "Phone" : "Phone (optional)"}
              required={apt?.phone_required}
              type="tel"
              inputMode="tel"
              value={formValues._phone || ""}
              onChange={(v) => setFormValues({ ...formValues, _phone: v.replace(/[^0-9+\-()\s]/g, "") })}
              testid="pub-phone"
            />
            {/* Comments field hidden per request (v2.99) — intentionally NOT
                deleted so it can be re-enabled later. To restore, uncomment
                the line below. While hidden, `_notes` simply stays empty. */}
            {/* <FieldInput label="Comments (optional)" textarea value={formValues._notes || ""} onChange={(v) => setFormValues({ ...formValues, _notes: v })} testid="pub-notes" /> */}
          </div>

          {fields.length > 0 && (
            <div className="mt-6">
              <div className="grid sm:grid-cols-2 gap-3">
                {fields.map((f) => (
                  <DynamicField key={f.field_id} field={f} value={formValues[f.field_id]} onChange={(v) => setFormValues({ ...formValues, [f.field_id]: v })} />
                ))}
              </div>
            </div>
          )}

          {addons.length > 0 && (
            <div className="mt-6">
              <p className="text-sm uppercase tracking-widest text-[#82828c] mb-2">Add-ons</p>
              <div className="flex flex-col gap-2">
                {addons.map((a) => {
                  const on = selectedAddons.includes(a.addon_id);
                  return (
                    <label key={a.addon_id} className="flex items-center gap-3 p-3 rounded-md cursor-pointer hover:bg-[#f5efe6] border border-[#1a1a1f]/06">
                      <input type="checkbox" checked={on} onChange={() => setSelectedAddons((arr) => on ? arr.filter((x) => x !== a.addon_id) : [...arr, a.addon_id])} data-testid={`pub-addon-${a.addon_id}`} />
                      <div className="flex-1">
                        <p className="text-base font-medium">{a.name}</p>
                        <p className="text-sm text-[#82828c]">{a.price_delta_amount > 0 ? `+$${a.price_delta_amount}` : a.price_delta_amount < 0 ? `-$${Math.abs(a.price_delta_amount)}` : ""} {a.duration_delta_minutes ? `· ${a.duration_delta_minutes > 0 ? "+" : ""}${a.duration_delta_minutes}min` : ""}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting} className="bloq-btn-primary mt-7 w-full inline-flex items-center justify-center gap-2 text-base py-3" data-testid="pub-submit">
            {submitting ? "Booking…" : (info.requires_approval ? "Request booking" : info.requires_payment ? "Continue to payment" : "Confirm booking")} <ArrowRight className="w-5 h-5" />
          </button>
          {info.requires_payment && (
            <p className="text-sm text-[#b45309] mt-2 text-center">Note: Payment processing is not yet configured by the host. Status will be 'pending' until they reach out.</p>
          )}
        </div>
      </PublicShell>
    );
  }

  // ---- PICK step (3-column layout) ------------------------------------------
  const minViewMonth = startOfMonth(new Date()).getTime();
  const maxViewMonth = startOfMonth(addMonths(new Date(), maxFutureMonths)).getTime();
  const canPrev = viewDate.getTime() > minViewMonth;
  const canNext = viewDate.getTime() < maxViewMonth;
  // Suppress unused-var lint for hostTz now that the host-tz hint is removed.
  void hostTz;

  return (
    <PublicShell>
      <div className="max-w-6xl mx-auto">
        <StepStrip
          steps={buildStepList({ apptTypes, aptLocations })}
          currentStep={
            apptTypes.length > 1 && !selectedAptId ? "apt"
            : needsLocationPick ? "loc"
            : "pick"
          }
          onNavigate={(target) => navigateToStep({
            target,
            setStep,
            setSelectedSlot,
            setSelectedDay,
            setSelectedAptId,
            setSelectedLocationId,
            apptTypes,
            aptLocations,
          })}
        />
        <div className="grid lg:grid-cols-[300px_1fr] gap-6">
          {/* LEFT — event details */}
          <aside className="rounded-2xl p-7 bg-white border border-[#1a1a1f]/8 h-fit lg:sticky lg:top-6" data-testid="pub-aside">
            {/* Host block — only renders when the workspace has a real host
                name set. Avoids '?' avatar + duplicated title on bare seeds. */}
            {info.host_name && info.host_name.toLowerCase() !== "host" && info.host_name !== info.title && (
              <div className="flex items-center gap-3 mb-1">
                {info.host_avatar ? (
                  <Avatar className="w-14 h-14 rounded-full">
                    <AvatarImage src={info.host_avatar} />
                    <AvatarFallback>{info.host_name?.[0]}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#005e7a]/10 text-[#005e7a] flex items-center justify-center font-semibold text-lg">{info.host_name?.[0] || "?"}</div>
                )}
                <div className="min-w-0">
                  <p className="text-base font-medium text-[#1a1a1f] truncate" data-testid="pub-host-name">{info.host_name}</p>
                </div>
              </div>
            )}

            <p className="bloq-headline text-2xl mt-5">{info.title}</p>
            {/* When the page is dedicated to a single appointment type
                (the /a/<slug> standalone page, or a link with exactly
                one appointment type attached), render the host's
                uploaded image as a hero above the description. Same
                4:3 aspect as the multi-type card thumbnails so the
                visual style stays consistent across both entry-points. */}
            {apt && apptTypes.length === 1 && apt.image_url && (
              <div className="aspect-[4/3] mt-4 -mx-7 sm:mx-0 overflow-hidden sm:rounded-xl bg-[#f5efe6]">
                <img
                  src={apt.image_url}
                  alt=""
                  loading="eager"
                  className="w-full h-full object-cover"
                  data-testid="pub-apt-hero-image"
                />
              </div>
            )}
            {info.description && <p className="text-base text-[#4a4a52] mt-3 whitespace-pre-line leading-relaxed">{info.description}</p>}

            {/* Chosen appointment-type name + Change link(s) — surfaced inside
                the left card so multi-type booking links can show which
                option the visitor picked. The badge row that used to sit
                above the monthly calendar has been removed entirely; this
                block is the only place a "Change appointment" / "Change
                location" affordance still lives.

                Strict gate: ONLY render when the visitor has explicitly
                clicked something (AT or location). Auto-selected single
                options (single-AT link, single-loc auto-pick) do NOT
                count — the aside stays clean until the visitor makes a
                real choice. */}
            {apt && (
              (userPickedApt && apptTypes.length > 1) ||
              (userPickedLocation && aptLocations.length > 1)
            ) && (
              <div className="mt-4 flex items-center justify-between gap-2 flex-wrap" data-testid="pub-aside-apt-chosen">
                {userPickedApt && apptTypes.length > 1 ? (
                  <span
                    className="bloq-pill text-sm"
                    style={{ backgroundColor: (apt.color || "#005e7a") + "1a", color: apt.color || "#005e7a" }}
                    data-testid="pub-aside-apt-pill"
                  >
                    {apt.name}
                  </span>
                ) : (
                  // Spacer so the Change-location button right-aligns when
                  // there's no AT pill on the left (single-AT scenario).
                  <span />
                )}
                <div className="flex items-center gap-3">
                  {apptTypes.length > 1 && (
                    <button
                      onClick={() => {
                        setSelectedAptId(null);
                        setSelectedSlot(null);
                        setSelectedDay(null);
                        setSelectedLocationId(null);
                        setUserPickedApt(false);
                        setUserPickedLocation(false);
                      }}
                      className="text-xs text-[#005e7a] hover:underline"
                      data-testid="pub-aside-apt-change-btn"
                    >
                      Change
                    </button>
                  )}
                  {aptLocations.length > 1 && selectedLocationId && (
                    <button
                      onClick={() => { setSelectedLocationId(null); setSelectedSlot(null); setSelectedDay(null); setUserPickedLocation(false); }}
                      className="text-xs text-[#005e7a] hover:underline"
                      data-testid="pub-aside-location-change-btn"
                    >
                      Change location
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Chosen appointment-type description — renders in two
                scenarios, both purely additive (info.description above
                still renders independently):
                  • Multi-AT booking link → only AFTER the visitor has
                    explicitly clicked an appointment-type pick. Sits
                    directly below the AT name + Change row.
                  • Single-AT booking link → automatically (no click
                    needed, since there's nothing to click). Sits
                    directly under the title / hero / link description
                    so the AT-level copy is always visible to visitors.
                Reads from `apt.description` which the backend already
                surfaces on info.appointment_types[] / info.appointment_type
                — no AT or link code changes required.

                Excluded: `mode === "appointment"` (i.e. /a/<slug> direct
                appointment-type links). The backend's public-appointment
                endpoint returns `appointment_types: [apt]` (length 1)
                so this block would otherwise fire — but on that page
                `info.description` IS already the AT description, and
                rendering it twice was the duplicate-description bug. */}
            {apt?.description && mode !== "appointment" && (
              (userPickedApt && apptTypes.length > 1) ||
              (apptTypes.length === 1)
            ) && (
              <p
                className="text-base text-[#4a4a52] mt-3 whitespace-pre-line leading-relaxed"
                data-testid="pub-aside-apt-description"
              >
                {apt.description}
              </p>
            )}

            {/* Duration / location / price block — strict gate: ONLY
                renders after the visitor has explicitly picked something.
                Two scenarios trigger it:
                  • The visitor clicked a location button (covers single-AT
                    multi-loc and multi-AT multi-loc flows after pick).
                  • The visitor clicked an appointment type that has 1 or
                    0 locations (multi-AT into a single-loc AT — no
                    location pick is required, but the AT click already
                    counts as explicit intent).
                Single-AT single-loc and /a/<slug> never trigger because
                no explicit pick is ever required of the visitor. The full
                booking summary still renders on the "Confirm booking"
                step after a slot is selected. */}
            {apt && (userPickedLocation || (userPickedApt && aptLocations.length <= 1) || (mode === "appointment" && aptLocations.length === 1)) && (
              <div className="flex flex-col gap-3 mt-5 text-base text-[#4a4a52]">
                <span className="inline-flex items-center gap-2"><Clock className="w-5 h-5 text-[#005e7a]" /> {formatDuration(apt.duration_minutes)}</span>
                <span className="inline-flex items-start gap-2">
                  <EffectiveLocIcon className="w-5 h-5 text-[#005e7a] mt-0.5 flex-shrink-0" />
                  <span className="flex flex-col">
                    <span>{effectiveLocationLabel}</span>
                    {effectiveLocationSub && <span className="text-sm text-[#82828c]">{effectiveLocationSub}</span>}
                  </span>
                </span>
                {apt.price_amount > 0 && (
                  <span className="inline-flex items-center gap-2 text-[#b45309] font-medium">
                    {(apt.sale_price_amount != null && apt.sale_price_amount !== apt.price_amount) ? (
                      <><span className="line-through opacity-60">{apt.price_currency} ${apt.price_amount}</span> {apt.price_currency} ${apt.sale_price_amount}</>
                    ) : (
                      <>{apt.price_currency} ${apt.price_amount}</>
                    )}
                  </span>
                )}
              </div>
            )}

            {/* Map for in-person/hybrid — same strict gate as the
                duration/location block. No auto-rendering of any default
                location map before the visitor explicitly picks. */}
            {(userPickedLocation || (userPickedApt && aptLocations.length <= 1))
              && effectiveLocation.record
              && ["in_person", "hybrid"].includes(effectiveLocation.record.type)
              && effectiveLocation.record.map_embed_url && (
              <div className="mt-4 rounded-lg overflow-hidden border border-[#1a1a1f]/06" data-testid="pub-map">
                <iframe
                  title="Location map"
                  src={effectiveLocation.record.map_embed_url}
                  className="w-full h-44 block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                {effectiveLocation.record.map_link && (
                  <a
                    href={effectiveLocation.record.map_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-xs text-center py-2 text-[#005e7a] bg-[#f5efe6] hover:bg-[#005e7a]/10 transition-colors"
                  >
                    Open in maps ↗
                  </a>
                )}
              </div>
            )}

            {info.requires_approval && (
              <p className="text-sm text-[#b45309] mt-4 flex items-start gap-1.5"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> Requires host approval before confirmation.</p>
            )}
          </aside>

          {/* RIGHT — Calendar + Times in ONE box */}
          <section className="rounded-2xl bg-white border border-[#1a1a1f]/8 overflow-hidden" data-testid="pub-scheduler">
            {/* Bundle B — Step 0: appointment-type picker. Only rendered when
                the link has 2+ appointment types attached. Auto-skips for
                single-type links (existing behavior, no visual change). */}
            {apptTypes.length > 1 && !selectedAptId && (
              <div className="p-7" data-testid="pub-apt-picker">
                <p className="bloq-headline text-xl mb-2">What would you like to book?</p>
                <p className="text-base text-[#82828c] mb-5">Pick an appointment type to see available times.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {apptTypes.map((a) => (
                    <button
                      key={a.appointment_type_id}
                      onClick={() => {
                        setSelectedAptId(a.appointment_type_id);
                        setSelectedSlot(null); setSelectedDay(null);
                        setUserPickedApt(true);
                        // Reset the location-pick flag — picking a new AT
                        // restarts the location step from scratch.
                        setUserPickedLocation(false);
                      }}
                      className="text-left p-5 rounded-xl border border-[#1a1a1f]/8 hover:border-[#005e7a] hover:bg-[#f5efe6] transition-colors group"
                      data-testid={`pub-apt-option-${a.appointment_type_id}`}
                      style={{ borderLeftWidth: 4, borderLeftColor: a.color || "#005e7a" }}
                    >
                      {/* Appointment-type thumbnail. Renders at 4:3 above
                          the title so square uploads get a clean
                          center-cropped letterbox feel and wider uploads
                          fit naturally. Same ratio as the public /a/<slug>
                          hero so the host's branding stays consistent. */}
                      {a.image_url && (
                        <div className="aspect-[4/3] mb-3 -mx-5 -mt-5 overflow-hidden rounded-t-xl border-b border-[#1a1a1f]/8 bg-[#f5efe6]">
                          <img
                            src={a.image_url}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                            data-testid={`pub-apt-image-${a.appointment_type_id}`}
                          />
                        </div>
                      )}
                      <p className="bloq-headline text-lg group-hover:text-[#005e7a]">{a.name}</p>
                      <p className="text-base text-[#82828c] mt-1.5">
                        {formatDuration(a.duration_minutes)} · {a.price_amount > 0
                          ? (a.sale_price_amount != null && a.sale_price_amount !== a.price_amount
                              ? <><span className="line-through opacity-60">{a.price_currency} {a.price_amount}</span> {a.price_currency} {a.sale_price_amount}</>
                              : `${a.price_currency} ${a.price_amount}`)
                          : "Free"}
                        {a.classification && a.classification !== "individual" && <> · {a.classification}</>}
                      </p>
                      {a.description && <p className="text-sm text-[#4a4a52] mt-2 line-clamp-2">{a.description}</p>}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* The "chosen appt-type + location badge" row that used to
                sit above the monthly calendar has been removed entirely
                in v2.61 — nothing renders here now. The selected
                appointment + Change controls live in the left aside card
                ("pub-aside-apt-chosen") so the calendar gets the full
                width of its column without a header strip pushing it
                down. */}
            {/* Bundle B.1 — Location picker. Renders when an appt is
                chosen AND has 2+ locations attached. Stays inline so the
                whole flow lives on one page (no popups, no new windows). */}
            {selectedAptId && needsLocationPick && (
              <div className="p-7 border-t border-[#1a1a1f]/8" data-testid="pub-location-picker">
                {/* Back-to-services button — only renders for multi-AT
                    links so visitors who pick the wrong service can
                    rewind one step without losing their place. Hidden
                    on single-AT links because there's no service step
                    to return to. */}
                {apptTypes.length > 1 && (
                  <button
                    onClick={() => {
                      setSelectedAptId(null);
                      setSelectedLocationId(null);
                      setSelectedSlot(null);
                      setSelectedDay(null);
                      setUserPickedApt(false);
                      setUserPickedLocation(false);
                    }}
                    className="inline-flex items-center gap-1.5 text-sm text-[#005e7a] hover:underline mb-4"
                    data-testid="pub-location-back-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Back to services
                  </button>
                )}
                <p className="bloq-headline text-xl mb-2">Where would you like to meet?</p>
                <p className="text-base text-[#82828c] mb-5">Pick a location to see available times.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {aptLocations.map((L) => {
                    const LocIcon = APT_LOC_ICON[L.type] || MapPin;
                    return (
                    <button
                      key={L.location_id}
                      onClick={() => { setSelectedLocationId(L.location_id); setSelectedSlot(null); setSelectedDay(null); setUserPickedLocation(true); }}
                      className="text-left p-5 rounded-xl border border-[#1a1a1f]/8 hover:border-[#005e7a] hover:bg-[#f5efe6] transition-colors group"
                      data-testid={`pub-location-option-${L.location_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#005e7a]/10 text-[#005e7a] flex items-center justify-center shrink-0 group-hover:bg-[#005e7a] group-hover:text-white transition-colors">
                          <LocIcon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="bloq-headline text-lg group-hover:text-[#005e7a]">{L.name}</p>
                          <p className="text-sm text-[#82828c] mt-1 capitalize">{(L.type || "").replace("_", " ")}</p>
                          {L.address_formatted && <p className="text-sm text-[#82828c] mt-1 truncate">{L.address_formatted}</p>}
                          {L.custom_instructions && <p className="text-sm text-[#4a4a52] mt-2 line-clamp-2">{L.custom_instructions}</p>}
                        </div>
                      </div>
                    </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Hide the calendar entirely until an appointment type is picked
                on multi-type links AND a location is picked when multiple
                are available — otherwise we'd show slots without the
                visitor having made a meaningful choice. */}
            {!(apptTypes.length > 1 && !selectedAptId) && !needsLocationPick && (
            <div className="grid lg:grid-cols-[1fr_280px] lg:items-stretch">
              {/* Calendar pane */}
              <div ref={calendarRef} className="p-7 lg:border-r border-[#1a1a1f]/06" data-testid="pub-calendar">
                {/* Timezone selector — prominent */}
                <div className="flex items-center justify-between gap-4 mb-6">
                  <button
                    onClick={() => canPrev && setViewDate((d) => addMonths(d, -1))}
                    disabled={!canPrev}
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center hover:bg-[#f5efe6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="pub-prev-month"
                    aria-label="Previous month"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <p className="bloq-headline text-2xl flex-1 text-center">
                    {viewDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </p>
                  <button
                    onClick={() => canNext && setViewDate((d) => addMonths(d, 1))}
                    disabled={!canNext}
                    className="w-10 h-10 rounded-full inline-flex items-center justify-center hover:bg-[#f5efe6] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="pub-next-month"
                    aria-label="Next month"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-xs uppercase tracking-widest text-[#82828c] mb-2">
                  {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
                    <div key={i} className="text-center py-1">{d}</div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1.5">
                  {grid.map((day, idx) => {
                    const slots = slotsByDay.get(day.key) || [];
                    const isPast = day.key < today;
                    const disabled = !day.inMonth || isPast || slots.length === 0;
                    const isSelected = selectedDay === day.key;
                    const isToday = day.key === today;
                    return (
                      <button
                        key={idx}
                        onClick={() => { if (!disabled) { setSelectedDay(day.key); setSelectedSlot(null); scrollToTimesOnMobile(); } }}
                        disabled={disabled}
                        className={`relative aspect-square rounded-lg text-base font-medium transition-all
                          ${isSelected ? "bg-[#005e7a] text-white shadow-md scale-105"
                            : disabled ? "text-[#c4c4cb] cursor-not-allowed"
                            : "text-[#1a1a1f] hover:bg-[#005e7a]/10 hover:text-[#005e7a] bg-[#f5efe6]/50"}
                          ${!day.inMonth ? "opacity-30" : ""}
                        `}
                        data-testid={`pub-day-${day.key}`}
                      >
                        <span>{day.date.getDate()}</span>
                        {isToday && !isSelected && (
                          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#005e7a]" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <p className="text-sm text-[#82828c] mt-6 text-center">
                  {loadingSlots ? "Loading availability…" : (
                    monthSlots.length === 0
                      ? `No times available in ${viewDate.toLocaleDateString("en-US", { month: "long" })}. Try another month.`
                      /* Bottom hint intentionally blank when slots are available
                         — the times pane already shows "Select a date to see
                         times." so duplicating the prompt under the calendar
                         was redundant. */
                      : ""
                  )}
                </p>
              </div>

              {/* Times pane — On desktop (lg+) visually attached to the calendar's
                  right edge; same height as the calendar via CSS grid
                  `items-stretch` + absolute-positioned inner column. On mobile/
                  tablet (<lg), the grid stacks to one column and the absolute
                  inset-0 trick collapses to 0px height — so we gate the
                  `absolute inset-0` to lg+ and fall back to normal flex flow
                  on smaller screens (times pane renders below the calendar).
                  See CHANGELOG v2.87 for the regression root cause. */}
              <div
                ref={timesRef}
                className="bg-[#fafafa] relative"
                data-testid="pub-times"
              >
                <div className="flex flex-col lg:absolute lg:inset-0">
                  {/* Timezone picker */}
                  <div className="p-5 border-b border-[#1a1a1f]/06 shrink-0">
                    <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium block mb-1.5 inline-flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> Your timezone
                    </label>
                    <Select value={tz} onValueChange={setTz}>
                      <SelectTrigger className="w-full text-sm h-10" data-testid="pub-tz">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {COMMON_TIMEZONES.map((z) => (
                          <SelectItem key={z} value={z}>{tzLabelWithOffset(z)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Selected day header */}
                  <div className="p-5 pb-2 shrink-0">
                    {selectedDay ? (
                      <>
                        <p className="text-base font-medium text-[#1a1a1f]">
                          {formatLongDateInTz(`${selectedDay}T12:00:00Z`, tz)}
                        </p>
                        <p className="text-xs uppercase tracking-widest text-[#82828c] mt-1">
                          {(slotsByDay.get(selectedDay) || []).length} time{(slotsByDay.get(selectedDay) || []).length === 1 ? "" : "s"} available
                        </p>
                      </>
                    ) : (
                      <p className="text-base text-[#82828c]">Select a date to see times.</p>
                    )}
                  </div>

                  {/* Times list — mobile caps height at 60vh so a slot-heavy
                      day doesn't push the page into a long scroll; desktop
                      keeps the original flex-1 + min-h-0 + internal scroll
                      behavior. */}
                  {selectedDay && (
                    <div className="max-h-[60vh] overflow-y-auto lg:flex-1 lg:max-h-none px-5 pb-5 space-y-2 lg:min-h-0">
                      {(slotsByDay.get(selectedDay) || []).map((s) => {
                        const local = formatTime12hInTz(s.start_utc, tz);
                        const sel = selectedSlot?.start_utc === s.start_utc;
                        return (
                          <button
                            key={s.start_utc}
                            onClick={() => { setSelectedSlot(s); setStep("form"); }}
                            className={`w-full px-4 py-3 rounded-lg text-base font-medium border transition-all
                              ${sel
                                ? "bg-[#005e7a] text-white border-[#005e7a]"
                                : "bg-white text-[#1a1a1f] border-[#1a1a1f]/10 hover:border-[#005e7a] hover:text-[#005e7a]"}
                            `}
                            data-testid={`pub-slot-${s.start_utc}`}
                          >
                            {local}
                          </button>
                        );
                      })}
                      {(slotsByDay.get(selectedDay) || []).length === 0 && (
                        <p className="text-sm text-[#82828c] py-4 text-center">No times available on this date.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
          </section>
        </div>
      </div>
    </PublicShell>
  );
}

// --- Subcomponents ----------------------------------------------------------
function FieldInput({ label, value, onChange, required, type = "text", textarea, testid, inputMode }) {
  return (
    <div className={`pt-[7px] ${textarea ? "sm:col-span-2" : ""}`}>
      <label className="text-sm uppercase tracking-widest text-[#82828c] font-medium block mb-1.5">{label}{required && <span className="text-[#c43838]"> *</span>}</label>
      {textarea ? (
        <textarea rows={2} className="bloq-input text-base" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      ) : (
        <input type={type} inputMode={inputMode} className="bloq-input text-base" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      )}
    </div>
  );
}

function DynamicField({ field, value, onChange }) {
  // Each dynamic question gets a fixed 7px top padding so the labels don't
  // sit flush against the previous question's input. Applied at the row
  // root so it works for both full-width and half-width fields.
  const cls = "sm:col-span-2 pt-[7px]";
  const cls1 = "pt-[7px]";
  const t = field.field_type;
  const Label = () => (
    <label className="text-sm uppercase tracking-widest text-[#82828c] block mb-1.5">
      {field.label}{field.required && <span className="text-[#c43838]"> *</span>}
    </label>
  );
  const Help = () => field.help_text ? (
    <p className="text-xs text-[#82828c] mt-1">{field.help_text}</p>
  ) : null;
  // Rich text — display-only, no input collected. Body is HTML produced
  // by the TipTap editor in the admin form builder. We deliberately do NOT
  // render `field.label` here — that label is an internal name for the
  // admin to find the row in the form builder; the body IS the visible
  // content (any heading the admin wants is authored inline in the RTE).
  if (t === "rich_text") {
    return (
      <div className={cls} data-testid={`pub-field-rich-${field.field_id}`}>
        <RichTextView html={field.body || ""} className="mt-1" />
      </div>
    );
  }
  if (t === "long_text") {
    return <div className={cls}><Label /><textarea rows={3} className="bloq-input text-base" value={value || ""} onChange={(e) => onChange(e.target.value)} /><Help /></div>;
  }
  if (t === "address") {
    // Bloq address standard: structured fields (line1, line2, city, state,
    // postal_code, country). Submitting writes the same shape; emails and
    // ICS rendering compose to a single line via AddressFields' helper.
    const val = (value && typeof value === "object" && !Array.isArray(value))
      ? value
      : null;
    return (
      <div className={cls}>
        <Label />
        <AddressFields value={val} onChange={onChange} testid={`pub-addr-${field.field_id}`} />
        <Help />
      </div>
    );
  }
  if (t === "number") {
    return <div className={cls1}><Label /><input type="number" className="bloq-input text-base" value={value ?? ""} onChange={(e) => onChange(e.target.value)} /><Help /></div>;
  }
  if (t === "price") {
    // Constrain the input width — currency amounts are never long, so a
    // full-width field reads as oversized. Keep it tight. The "$" prefix
    // sits in a 32px chip; the input itself accepts the value.
    return (
      <div className={cls1}>
        <Label />
        <div className="flex items-stretch max-w-[180px]">
          <span className="inline-flex w-8 items-center justify-center text-[#82828c] text-base bg-[#f5efe6] border border-r-0 border-[#1a1a1f]/15">$</span>
          <input type="number" step="0.01" min="0" className="bloq-input text-base rounded-l-none flex-1 min-w-0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        </div>
        <Help />
      </div>
    );
  }
  if (t === "dropdown") {
    return (
      <div className={cls1}>
        <Label />
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger className="text-base"><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Help />
      </div>
    );
  }
  if (t === "radio") {
    const isOther = field.allow_other && value && typeof value === "object" && value.__other__ !== undefined;
    const otherSelected = field.allow_other && (value === "__other__" || isOther);
    return (
      <div className={cls} data-testid={`pub-field-radio-${field.field_id}`}>
        <Label />
        {/* 3-column grid keeps tall option lists compact. Falls back to
            2-col on smaller widths and 1-col on phones. Vertical gap is
            tighter than horizontal so the choices read as a group. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-1">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-base">
              <input type="radio" checked={value === o} onChange={() => onChange(o)} />{o}
            </label>
          ))}
          {field.allow_other && (
            <label className="flex items-center gap-2 text-base">
              <input
                type="radio"
                checked={otherSelected}
                onChange={() => onChange({ __other__: "" })}
              />Other
            </label>
          )}
        </div>
        {otherSelected && (
          <input
            type="text"
            className="bloq-input text-base ml-6 mt-2"
            placeholder="Please specify…"
            value={isOther ? value.__other__ : ""}
            onChange={(e) => onChange({ __other__: e.target.value })}
          />
        )}
        <Help />
      </div>
    );
  }
  if (t === "multi_checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className={cls} data-testid={`pub-field-multi-${field.field_id}`}>
        <Label />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2 gap-y-1">
          {(field.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-base">
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} />{o}
            </label>
          ))}
        </div>
        <Help />
      </div>
    );
  }
  if (t === "checkbox" || t === "consent") {
    return (
      <label className={`${cls} flex items-start gap-2 text-base`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
        <span>{field.label}{field.required && <span className="text-[#c43838]"> *</span>}</span>
      </label>
    );
  }
  if (t === "date") {
    return <div className={cls1}><Label /><input type="date" className="bloq-input text-base" value={value || ""} onChange={(e) => onChange(e.target.value)} /><Help /></div>;
  }
  return (
    <div className={cls1}>
      <Label />
      <input
        type={t === "email" ? "email" : t === "url" ? "url" : t === "phone" ? "tel" : "text"}
        inputMode={t === "phone" ? "tel" : undefined}
        className="bloq-input text-base"
        value={value || ""}
        onChange={(e) => {
          // Strip non-phone chars on the fly for the phone field type so
          // custom intake-form phone fields enforce the same rule as the
          // default phone input above.
          const next = t === "phone" ? e.target.value.replace(/[^0-9+\-()\s]/g, "") : e.target.value;
          onChange(next);
        }}
        placeholder={field.placeholder || ""}
      />
      <Help />
    </div>
  );
}

function PublicShell({ children }) {
  // Top header removed — the workspace logo will live elsewhere later.
  // Reduced top padding so the booking module sits closer to the top.
  return (
    <div className="min-h-screen w-full bg-white pt-6 pb-8 px-4">
      {children}
    </div>
  );
}

/**
 * Build the ordered list of steps for this booking flow. Steps are derived
 * from the link/appt context so single-appt + single-location flows don't
 * see ghost "Choose appointment" / "Choose location" steps.
 *
 * Returns: [{ key, label }]
 */
function buildStepList({ apptTypes, aptLocations }) {
  const steps = [];
  if (apptTypes && apptTypes.length > 1) {
    steps.push({ key: "apt", label: "Service" });
  }
  if (aptLocations && aptLocations.length > 1) {
    steps.push({ key: "loc", label: "Location" });
  }
  steps.push({ key: "pick", label: "Pick a time" });
  steps.push({ key: "form", label: "Your details" });
  steps.push({ key: "done", label: "Done" });
  return steps;
}

/**
 * Navigate the visitor backward in the booking flow. Forward navigation is
 * automatic (driven by their selections). The strip's tap targets are
 * disabled when navigating forward would skip an unfilled step.
 */
function navigateToStep({
  target, setStep, setSelectedSlot, setSelectedDay,
  setSelectedAptId, setSelectedLocationId,
}) {
  if (target === "apt") {
    setSelectedAptId(null);
    setSelectedLocationId(null);
    setSelectedSlot(null);
    setSelectedDay(null);
    setStep("pick");
  } else if (target === "loc") {
    setSelectedLocationId(null);
    setSelectedSlot(null);
    setSelectedDay(null);
    setStep("pick");
  } else if (target === "pick") {
    setSelectedSlot(null);
    setStep("pick");
  } else if (target === "form") {
    setStep("form");
  }
}

/**
 * Calendly-style step strip. Renders a horizontal breadcrumb of the
 * booking flow with back navigation. Forward navigation is intentionally
 * disabled — visitors must complete each step in order.
 */
function StepStrip({ steps, currentStep, onNavigate }) {
  const currentIdx = steps.findIndex((s) => s.key === currentStep);
  return (
    <nav
      className="max-w-6xl mx-auto mb-6 flex items-center gap-1 flex-wrap text-sm"
      aria-label="Booking progress"
      data-testid="pub-step-strip"
    >
      {steps.map((s, i) => {
        const isCurrent = i === currentIdx;
        const isComplete = i < currentIdx;
        const canGoBack = isComplete && onNavigate;
        return (
          <div key={s.key} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => canGoBack && onNavigate(s.key)}
              disabled={!canGoBack}
              className={`px-3 py-1.5 rounded-md transition-colors
                ${isCurrent ? "bg-[#005e7a] text-white font-medium"
                  : isComplete ? "text-[#005e7a] hover:bg-[#005e7a]/10 cursor-pointer"
                  : "text-[#82828c] cursor-default"}`}
              data-testid={`pub-step-${s.key}`}
              aria-current={isCurrent ? "step" : undefined}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-xs font-semibold
                  ${isCurrent ? "bg-white text-[#005e7a]"
                    : isComplete ? "bg-[#005e7a]/15 text-[#005e7a]"
                    : "bg-[#1a1a1f]/06 text-[#82828c]"}`}>{i + 1}</span>
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <ChevronRight className="w-4 h-4 text-[#c4c4cb] shrink-0" />
            )}
          </div>
        );
      })}
    </nav>
  );
}
